import { theme as antdTheme, type ThemeConfig } from 'antd';

/**
 * Six distinct themes — three light, three dark — designed so a swap feels like
 * a personality change, not a recolor. Each theme owns:
 *   - AntD ConfigProvider tokens (colors, radius, sizing)
 *   - A font stack (body + heading); non-system fonts are lazy-loaded from
 *     Google Fonts by <ThemeProvider> on selection
 *   - A `bodyAttr` slug applied as `<html data-theme="…">` so themes/global.css
 *     can layer per-theme overrides (sider gradient, hover ring, selection,
 *     print rules, etc.) outside of AntD's reach
 *   - Shape / density / shadow style flags consumed by primitives
 *
 * If you add a theme: append to `ThemeKey`, fill out all required fields, and
 * mirror the bodyAttr in `themes/global.css`.
 */
export type ThemeKey =
  | 'obsidian'
  | 'blossom'
  | 'sandstone'
  | 'emerald'
  | 'cloudPro'
  | 'midnightNeon';

export type ThemeShape = 'sharp' | 'soft' | 'rounded' | 'pill';
export type ShadowStyle = 'hard' | 'paper' | 'soft' | 'glow';

export interface ThemeFontStack {
  body: string;
  heading: string;
  mono?: string;
  /** Google Fonts CSS2 URL — omitted for themes that rely on the system stack. */
  googleFontsHref?: string;
}

export interface SchoolTheme {
  key: ThemeKey;
  label: string;
  mode: 'light' | 'dark';
  character: string;
  /** [background, primary, accent] used by the picker swatch row. */
  swatch: [string, string, string];
  config: ThemeConfig;
  chartColors: string[];
  fontStack: ThemeFontStack;
  shape: ThemeShape;
  shadowStyle: ShadowStyle;
  /** Hook for themes/global.css → `[data-theme="<bodyAttr>"]`. */
  bodyAttr: string;
}

const dark = antdTheme.darkAlgorithm;
const light = antdTheme.defaultAlgorithm;

/* Font stacks — system fallbacks ensure legibility before remote fonts load. */
const systemBody =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
const systemMono =
  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace';

const FONTS = {
  obsidian: {
    body: `Inter, ${systemBody}`,
    heading: `Inter, ${systemBody}`,
    mono: systemMono,
    googleFontsHref:
      'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap',
  },
  blossom: {
    body: `Quicksand, ${systemBody}`,
    heading: `"DM Serif Display", Georgia, serif`,
    mono: systemMono,
    googleFontsHref:
      'https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=Quicksand:wght@400;500;600;700&display=swap',
  },
  sandstone: {
    body: `"Source Sans 3", ${systemBody}`,
    heading: `Lora, Georgia, serif`,
    mono: systemMono,
    googleFontsHref:
      'https://fonts.googleapis.com/css2?family=Lora:wght@500;600;700&family=Source+Sans+3:wght@400;500;600;700&display=swap',
  },
  emerald: {
    body: `Inter, ${systemBody}`,
    heading: `"Cormorant Garamond", Georgia, serif`,
    mono: systemMono,
    googleFontsHref:
      'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap',
  },
  cloudPro: {
    body: `Inter, ${systemBody}`,
    heading: `Inter, ${systemBody}`,
    mono: systemMono,
    googleFontsHref:
      'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
  },
  midnightNeon: {
    body: `"Space Grotesk", ${systemBody}`,
    heading: `"Space Grotesk", ${systemBody}`,
    mono: systemMono,
    googleFontsHref:
      'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap',
  },
} satisfies Record<ThemeKey, ThemeFontStack>;

const baseShared = {
  fontSize: 14,
  wireframe: false,
};

export const themes: Record<ThemeKey, SchoolTheme> = {
  /* ────────────────────────────  OBSIDIAN  ──────────────────────────── */
  obsidian: {
    key: 'obsidian',
    label: 'Obsidian',
    mode: 'dark',
    character: 'Industrial · Command',
    swatch: ['#0a0a0a', '#f59e0b', '#fb923c'],
    bodyAttr: 'obsidian',
    shape: 'sharp',
    shadowStyle: 'hard',
    fontStack: FONTS.obsidian,
    config: {
      algorithm: dark,
      cssVar: { key: 'school' },
      hashed: true,
      token: {
        ...baseShared,
        fontFamily: FONTS.obsidian.body,
        borderRadius: 4,
        colorPrimary: '#f59e0b',
        colorInfo: '#38bdf8',
        colorSuccess: '#22c55e',
        colorWarning: '#fbbf24',
        colorError: '#ef4444',
        colorBgBase: '#0a0a0a',
        colorBgLayout: '#0a0a0a',
        colorBgContainer: '#141414',
        colorBgElevated: '#1c1c1c',
        colorBorder: '#262626',
        colorBorderSecondary: '#1a1a1a',
        colorTextBase: '#f4f4f5',
        boxShadow:
          '0 1px 0 rgba(0,0,0,0.6), 0 4px 0 rgba(0,0,0,0.4)',
        boxShadowSecondary: '0 1px 0 rgba(0,0,0,0.6)',
      },
      components: {
        Layout: {
          headerBg: '#0f0f0f',
          siderBg: '#050505',
          bodyBg: '#0a0a0a',
          headerHeight: 60,
        },
        Menu: {
          darkItemBg: '#050505',
          darkSubMenuItemBg: '#050505',
          darkItemSelectedBg: '#3f2a08',
          darkItemHoverBg: '#171717',
        },
        Card: { colorBgContainer: '#141414' },
        Button: { borderRadius: 4 },
      },
    },
    chartColors: ['#f59e0b', '#fb923c', '#38bdf8', '#22c55e', '#a3a3a3', '#ef4444'],
  },

  /* ────────────────────────────  BLOSSOM  ──────────────────────────── */
  blossom: {
    key: 'blossom',
    label: 'Blossom',
    mode: 'light',
    character: 'Soft · Welcoming',
    swatch: ['#fff7fb', '#db2777', '#a855f7'],
    bodyAttr: 'blossom',
    shape: 'pill',
    shadowStyle: 'soft',
    fontStack: FONTS.blossom,
    config: {
      algorithm: light,
      cssVar: { key: 'school' },
      hashed: true,
      token: {
        ...baseShared,
        fontFamily: FONTS.blossom.body,
        borderRadius: 16,
        colorPrimary: '#db2777',
        colorInfo: '#a855f7',
        colorSuccess: '#16a34a',
        colorWarning: '#d97706',
        colorError: '#e11d48',
        colorBgBase: '#ffffff',
        colorBgLayout: '#fff5f9',
        colorBgContainer: '#ffffff',
        colorBgElevated: '#ffffff',
        colorBorder: '#fbcfe8',
        colorBorderSecondary: '#fde7f3',
        boxShadow:
          '0 8px 24px -8px rgba(219,39,119,0.22), 0 2px 8px -2px rgba(168,85,247,0.10)',
        boxShadowSecondary: '0 2px 8px -2px rgba(219,39,119,0.12)',
      },
      components: {
        Layout: {
          headerBg: '#ffffff',
          siderBg: '#9d174d',
          bodyBg: '#fff5f9',
          headerHeight: 60,
        },
        Menu: {
          darkItemBg: '#9d174d',
          darkSubMenuItemBg: '#9d174d',
          darkItemSelectedBg: '#6b1136',
          darkItemHoverBg: '#b21d5a',
        },
        Button: { borderRadius: 999 },
        Card: { borderRadiusLG: 18 },
      },
    },
    chartColors: ['#db2777', '#a855f7', '#f472b6', '#0ea5e9', '#16a34a', '#f59e0b'],
  },

  /* ────────────────────────────  SANDSTONE  ──────────────────────────── */
  sandstone: {
    key: 'sandstone',
    label: 'Sandstone',
    mode: 'light',
    character: 'Editorial · Warm',
    swatch: ['#faf6f0', '#b45309', '#0f766e'],
    bodyAttr: 'sandstone',
    shape: 'soft',
    shadowStyle: 'paper',
    fontStack: FONTS.sandstone,
    config: {
      algorithm: light,
      cssVar: { key: 'school' },
      hashed: true,
      token: {
        ...baseShared,
        fontFamily: FONTS.sandstone.body,
        borderRadius: 6,
        colorPrimary: '#b45309',
        colorInfo: '#0f766e',
        colorSuccess: '#15803d',
        colorWarning: '#a16207',
        colorError: '#b91c1c',
        colorBgBase: '#faf6f0',
        colorBgLayout: '#f5efe5',
        colorBgContainer: '#ffffff',
        colorBgElevated: '#fffaf3',
        colorBorder: '#e5d9c4',
        colorBorderSecondary: '#efe5d2',
        boxShadow:
          '0 1px 0 rgba(180,83,9,0.15), 0 4px 12px -6px rgba(120,53,15,0.18)',
        boxShadowSecondary: '0 1px 0 rgba(180,83,9,0.10)',
      },
      components: {
        Layout: {
          headerBg: '#fffaf3',
          siderBg: '#2c1810',
          bodyBg: '#f5efe5',
          headerHeight: 60,
        },
        Menu: {
          darkItemBg: '#2c1810',
          darkSubMenuItemBg: '#2c1810',
          darkItemSelectedBg: '#5e3a20',
          darkItemHoverBg: '#3d2415',
        },
        Card: { colorBgContainer: '#ffffff', borderRadiusLG: 8 },
      },
    },
    chartColors: ['#b45309', '#0f766e', '#a16207', '#7c2d12', '#365314', '#1e3a8a'],
  },

  /* ────────────────────────────  EMERALD COURT  ──────────────────────────── */
  emerald: {
    key: 'emerald',
    label: 'Emerald Court',
    mode: 'dark',
    character: 'Institutional · Prestige',
    swatch: ['#06281c', '#10b981', '#d4af37'],
    bodyAttr: 'emerald',
    shape: 'soft',
    shadowStyle: 'soft',
    fontStack: FONTS.emerald,
    config: {
      algorithm: dark,
      cssVar: { key: 'school' },
      hashed: true,
      token: {
        ...baseShared,
        fontFamily: FONTS.emerald.body,
        borderRadius: 8,
        colorPrimary: '#10b981',
        colorInfo: '#d4af37',
        colorSuccess: '#34d399',
        colorWarning: '#d4af37',
        colorError: '#f87171',
        colorBgBase: '#06281c',
        colorBgLayout: '#06281c',
        colorBgContainer: '#0c3a2a',
        colorBgElevated: '#114a36',
        colorBorder: '#1f5a44',
        colorBorderSecondary: '#0f3527',
        colorTextBase: '#e6f4ec',
        boxShadow:
          '0 2px 0 rgba(0,0,0,0.4), 0 0 0 1px rgba(212,175,55,0.10) inset',
        boxShadowSecondary: '0 2px 0 rgba(0,0,0,0.3)',
      },
      components: {
        Layout: {
          headerBg: '#08321f',
          siderBg: '#03190f',
          bodyBg: '#06281c',
          headerHeight: 60,
        },
        Menu: {
          darkItemBg: '#03190f',
          darkSubMenuItemBg: '#03190f',
          darkItemSelectedBg: '#0e5238',
          darkItemHoverBg: '#0a2a1d',
        },
        Card: { colorBgContainer: '#0c3a2a' },
      },
    },
    chartColors: ['#10b981', '#d4af37', '#34d399', '#5eead4', '#a3e635', '#f59e0b'],
  },

  /* ────────────────────────────  CLOUD PRO  ──────────────────────────── */
  cloudPro: {
    key: 'cloudPro',
    label: 'Cloud Pro',
    mode: 'light',
    character: 'Clean · SaaS',
    swatch: ['#ffffff', '#2563eb', '#0891b2'],
    bodyAttr: 'cloud-pro',
    shape: 'rounded',
    shadowStyle: 'soft',
    fontStack: FONTS.cloudPro,
    config: {
      algorithm: light,
      cssVar: { key: 'school' },
      hashed: true,
      token: {
        ...baseShared,
        fontFamily: FONTS.cloudPro.body,
        borderRadius: 10,
        colorPrimary: '#2563eb',
        colorInfo: '#0891b2',
        colorSuccess: '#16a34a',
        colorWarning: '#d97706',
        colorError: '#dc2626',
        colorBgBase: '#ffffff',
        colorBgLayout: '#f6f8fb',
        colorBgContainer: '#ffffff',
        colorBgElevated: '#ffffff',
        colorBorder: '#e2e8f0',
        colorBorderSecondary: '#eef2f6',
        boxShadow:
          '0 1px 2px rgba(15,23,42,0.04), 0 4px 12px -4px rgba(15,23,42,0.08)',
        boxShadowSecondary: '0 1px 2px rgba(15,23,42,0.04)',
      },
      components: {
        Layout: {
          headerBg: '#ffffff',
          siderBg: '#0f172a',
          bodyBg: '#f6f8fb',
          headerHeight: 60,
        },
        Menu: {
          darkItemBg: '#0f172a',
          darkSubMenuItemBg: '#0f172a',
          darkItemSelectedBg: '#1e40af',
          darkItemHoverBg: '#1e293b',
        },
      },
    },
    chartColors: ['#2563eb', '#0891b2', '#7c3aed', '#16a34a', '#d97706', '#dc2626'],
  },

  /* ────────────────────────────  MIDNIGHT NEON  ──────────────────────────── */
  midnightNeon: {
    key: 'midnightNeon',
    label: 'Midnight Neon',
    mode: 'dark',
    character: 'Modern · Energetic',
    swatch: ['#060818', '#22d3ee', '#d946ef'],
    bodyAttr: 'midnight-neon',
    shape: 'rounded',
    shadowStyle: 'glow',
    fontStack: FONTS.midnightNeon,
    config: {
      algorithm: dark,
      cssVar: { key: 'school' },
      hashed: true,
      token: {
        ...baseShared,
        fontFamily: FONTS.midnightNeon.body,
        borderRadius: 12,
        colorPrimary: '#22d3ee',
        colorInfo: '#d946ef',
        colorSuccess: '#4ade80',
        colorWarning: '#facc15',
        colorError: '#fb7185',
        colorBgBase: '#060818',
        colorBgLayout: '#060818',
        colorBgContainer: '#0d1130',
        colorBgElevated: '#13183f',
        colorBorder: '#252a55',
        colorBorderSecondary: '#181c3b',
        colorTextBase: '#e2e8ff',
        boxShadow:
          '0 0 0 1px rgba(34,211,238,0.18) inset, 0 0 24px -6px rgba(34,211,238,0.35)',
        boxShadowSecondary: '0 0 0 1px rgba(34,211,238,0.12) inset',
      },
      components: {
        Layout: {
          headerBg: '#0a0e26',
          siderBg: '#04061a',
          bodyBg: '#060818',
          headerHeight: 60,
        },
        Menu: {
          darkItemBg: '#04061a',
          darkSubMenuItemBg: '#04061a',
          darkItemSelectedBg: '#1f2666',
          darkItemHoverBg: '#0e1233',
        },
      },
    },
    chartColors: ['#22d3ee', '#d946ef', '#a78bfa', '#4ade80', '#facc15', '#fb7185'],
  },
};

export const themeList: SchoolTheme[] = Object.values(themes);

/** Maps obsolete pre-Phase-2 keys onto the closest new theme. */
const LEGACY_KEY_MAP: Record<string, ThemeKey> = {
  slateDark: 'obsidian',
  charcoalDark: 'sandstone',
  forestDark: 'emerald',
  cloudLight: 'cloudPro',
  steelLight: 'cloudPro',
  blossomLight: 'blossom',
};

export function resolveThemeKey(maybe: string | undefined): ThemeKey {
  if (maybe && maybe in themes) return maybe as ThemeKey;
  if (maybe && maybe in LEGACY_KEY_MAP) return LEGACY_KEY_MAP[maybe];
  return 'cloudPro';
}
